import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Calendar, dayjsLocalizer, Views, type EventProps, type View } from 'react-big-calendar'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'
import 'dayjs/locale/fi'
import 'dayjs/locale/sv'
import updateLocale from 'dayjs/plugin/updateLocale'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, useTheme } from '@mui/material/styles'
import { aircraftColor, formatTime, formatUtcOffset, getClubTimezone, timezoneLabel, useAuth, useClubLocation } from '@jupi/ui'
import type { Aircraft, Booking } from '../../api/queries'
import TenDay, { TEN_DAY_VIEW } from './TenDayView'
import { AOG_OPACITY, BARBER_POLE, bookingIsAog, type AircraftStatus } from './aircraftStatus'
import { BOOKING_TYPE_ICONS, typeOf } from './bookingType'
import { BookingHover, useBookingPeek } from './BookingPeek'
import { slotDaylight, SunLine } from './daylight'

dayjs.extend(updateLocale)
// Finland starts the week on Monday, and so do Sweden and the UK. rbc reads
// this off the localizer rather than taking a prop.
dayjs.updateLocale('fi', { weekStart: 1 })
dayjs.updateLocale('sv', { weekStart: 1 })
dayjs.updateLocale('en', { weekStart: 1 })

const localizer = dayjsLocalizer(dayjs)

// 24-hour clock with a dot, as everywhere else in the app (13.00): the
// grid's hour labels, event time ranges, slide-to-select and the agenda.
const hm = (date: Date | string) => dayjs(date).format('HH.mm')
const range = ({ start, end }: { start: Date; end: Date }) => `${hm(start)} – ${hm(end)}`
const formats = {
  timeGutterFormat: hm,
  eventTimeRangeFormat: range,
  eventTimeRangeStartFormat: ({ start }: { start: Date }) => `${hm(start)} –`,
  eventTimeRangeEndFormat: ({ end }: { end: Date }) => `– ${hm(end)}`,
  selectRangeFormat: range,
  agendaTimeFormat: hm,
  agendaTimeRangeFormat: range,
}

export interface CalendarEvent {
  title: string
  start: Date
  end: Date
  allDay: boolean
  booking: Booking
  colorIndex: number
  // An aircraft's AOG block (an all-day event, not a booking), and whether
  // a booking falls on AOG days.
  aog?: boolean
  // An aircraft's maintenance block: the yellow barber pole.
  maintenance?: boolean
  onAogDays?: boolean
  registration: string
}

export interface BookingCalendarProps {
  view: View
  date: Date
  bookings: Booking[]
  aircraft: Aircraft[]
  statuses?: Map<string, AircraftStatus>
  onOpenDefects?: (registration: string) => void
  onView: (view: View) => void
  onNavigate: (date: Date) => void
  onSelectBooking?: (booking: Booking) => void
  // `end` is set only when the member slid across a range of time slots;
  // a plain tap leaves it undefined so the form applies its default length.
  onSelectSlot?: (start: Date, end?: Date) => void
}

export default function BookingCalendar({
  view,
  date,
  bookings,
  aircraft,
  statuses,
  onOpenDefects,
  onView,
  onNavigate,
  onSelectBooking,
  onSelectSlot,
}: BookingCalendarProps) {
  const theme = useTheme()
  const { t, i18n } = useTranslation()
  const peek = useBookingPeek(onSelectBooking)
  const place = useClubLocation()

  // On a phone the week and ten-day grids keep a readable column width and
  // scroll sideways (see calendar.css). The scroller is the grid body; the
  // day-name header sits outside it, so it is kept in step here.
  const boxRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const box = boxRef.current
    if (!box) return
    const follow = (event: Event) => {
      const body = event.target as HTMLElement
      if (!body.classList?.contains('rbc-time-content')) return
      const header = box.querySelector<HTMLElement>('.rbc-time-header')
      if (header) header.scrollLeft = body.scrollLeft
    }
    // scroll does not bubble: listen in the capture phase.
    box.addEventListener('scroll', follow, true)
    return () => box.removeEventListener('scroll', follow, true)
  }, [])

  // Each aircraft keeps one colour across every view, so a member learns
  // "OH-CAM is the blue one" rather than re-reading labels.
  const colorIndexByAircraft = useMemo(() => {
    const map = new Map<string, number>()
    aircraft.forEach((plane, index) => map.set(plane.registration, index))
    return map
  }, [aircraft])

  // How options are shown depends on the view, because an option does not
  // occupy the aircraft and must never look like it does:
  //
  //   week / day  omitted. rbc divides a time column between concurrent
  //               events, so a booking with two options queued on it
  //               becomes three slivers of vertical text -- and the option
  //               visually takes the slot away from the booking that
  //               actually holds it. The fleet view shows them properly, as
  //               a hatched strip along the bottom of the lane.
  //   month       collapsed to one "+N jonossa" chip per aircraft-day.
  //               Month cells are far too small for hatching.
  //   agenda      listed in full: it is a list, there is room, and a member
  //               scanning the week wants to see what they are queued on.
  // Only the agenda lists options individually; week, day and month each
  // handle them their own way (or, for month, via optionSummary below).
  const listsOptions = view === Views.AGENDA
  // ...except your own: what you are queued on is what you look for, so it is
  // always shown, as a chip in the all-day strip (never a column sliver) and
  // not counted in the "+N waiting" chips.
  const myUserId = useAuth().user?.id
  const visibleBookings = useMemo(
    () => (listsOptions ? bookings : bookings.filter((b) => b.kind !== 'option')),
    [bookings, listsOptions],
  )

  const optionSummary = useMemo<CalendarEvent[]>(() => {
    if (view !== Views.MONTH) return []
    // One chip per aircraft per day, carrying the count.
    const counts = new Map<string, { day: Date; booking: Booking; count: number }>()
    for (const booking of bookings) {
      if (booking.kind !== 'option' || booking.userId === myUserId) continue
      const start = new Date(booking.startsAt)
      const key = `${booking.registration}:${start.toDateString()}`
      const existing = counts.get(key)
      if (existing) existing.count += 1
      else counts.set(key, { day: start, booking, count: 1 })
    }
    return [...counts.values()].map(({ day, booking, count }) => {
      const start = new Date(day)
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setHours(23, 59, 59, 999)
      return {
        title: `${booking.registration} · ${t('calendar.waiting', { count })}`,
        start,
        end,
        allDay: true,
        booking,
        colorIndex: colorIndexByAircraft.get(booking.registration) ?? 0,
        registration: booking.registration,
      }
    })
  }, [bookings, view, colorIndexByAircraft, myUserId, t])

  const ownOptionEvents = useMemo<CalendarEvent[]>(() => {
    if (listsOptions) return []
    return bookings
      .filter((b) => b.kind === 'option' && b.userId === myUserId)
      .map((booking) => ({
        title: `${formatTime(booking.startsAt)}–${formatTime(booking.endsAt)} ${booking.registration} (${t('booking.option')})`,
        start: new Date(booking.startsAt),
        end: new Date(booking.endsAt),
        allDay: true,
        booking,
        colorIndex: colorIndexByAircraft.get(booking.registration) ?? 0,
        registration: booking.registration,
      }))
  }, [bookings, listsOptions, myUserId, colorIndexByAircraft, t])

  const events = useMemo<CalendarEvent[]>(
    () =>
      visibleBookings.map((booking) => {
        const start = new Date(booking.startsAt)
        const end = new Date(booking.endsAt)
        return {
          // Registration first: in a week column the registration is what
          // identifies the row, and the name may be clipped.
          title: `${booking.registration}${booking.kind === 'option' ? ` (${t('booking.option')})` : ''} · ${booking.displayName}`,
          start,
          end,
          // A booking spanning a day boundary goes in the all-day strip
          // rather than being drawn as a 30-hour column.
          allDay: end.getTime() - start.getTime() >= 24 * 3600_000 || !isSameDay(start, end),
          booking,
          colorIndex: colorIndexByAircraft.get(booking.registration) ?? 0,
          registration: booking.registration,
          onAogDays: bookingIsAog(booking, statuses?.get(booking.registration)),
        }
      }),
    [visibleBookings, colorIndexByAircraft, statuses, t],
  )

  // Each grounded aircraft's AOG block, as an all-day event across its days
  // (open-ended once the period has lapsed: drawn a month past the view).
  const aogEvents = useMemo<CalendarEvent[]>(() => {
    const out: CalendarEvent[] = []
    for (const plane of aircraft) {
      const block = statuses?.get(plane.registration)?.aog
      if (!block) continue
      const start = new Date(`${block.from}T00:00:00`)
      const end = block.until ? new Date(`${block.until}T23:59:59`) : new Date(date.getTime() + 45 * 86_400_000)
      out.push({
        title: `${plane.registration} · AOG`,
        start,
        end,
        allDay: true,
        booking: { registration: plane.registration } as Booking,
        colorIndex: colorIndexByAircraft.get(plane.registration) ?? 0,
        aog: true,
        registration: plane.registration,
      })
    }
    return out
  }, [aircraft, statuses, date, colorIndexByAircraft])

  // Likewise for an aircraft in maintenance.
  const maintenanceEvents = useMemo<CalendarEvent[]>(() => {
    const out: CalendarEvent[] = []
    for (const plane of aircraft) {
      const block = statuses?.get(plane.registration)?.maintenance
      if (!block) continue
      out.push({
        title: `${plane.registration} · ${t('aircraft.inMaintenance')}`,
        start: new Date(`${block.from}T00:00:00`),
        end: block.until ? new Date(`${block.until}T23:59:59`) : new Date(date.getTime() + 45 * 86_400_000),
        allDay: true,
        booking: { registration: plane.registration } as Booking,
        colorIndex: colorIndexByAircraft.get(plane.registration) ?? 0,
        maintenance: true,
        registration: plane.registration,
      })
    }
    return out
  }, [aircraft, statuses, date, colorIndexByAircraft, t])

  const eventPropGetter = useCallback(
    (event: CalendarEvent) => {
      // An aircraft in maintenance, and a maintenance booking: the pole.
      if (event.maintenance || event.booking.isMaintenance) {
        return {
          style: {
            backgroundImage: BARBER_POLE,
            backgroundColor: 'transparent',
            border: `1px solid ${alpha('#37474f', 0.55)}`,
            color: theme.palette.text.primary,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
          },
        }
      }
      if (event.aog) {
        return {
          style: {
            backgroundColor: alpha(theme.palette.error.main, AOG_OPACITY),
            border: `1px solid ${alpha(theme.palette.error.dark, 0.6)}`,
            color: theme.palette.text.primary,
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
          },
        }
      }
      // A booking on AOG days stays, greyed out.
      if (event.onAogDays) {
        return {
          style: {
            backgroundColor: theme.palette.grey[500],
            border: '1px solid rgba(0,0,0,0.1)',
            color: theme.palette.getContrastText(theme.palette.grey[500]),
            opacity: 0.7,
            borderRadius: 6,
            fontSize: 12,
          },
        }
      }
      const base = aircraftColor(event.colorIndex)
      const isOption = event.booking.kind === 'option'
      return {
        style: {
          backgroundColor: isOption ? alpha(base, 0.25) : base,
          // Options read as provisional: translucent, dashed, and carrying
          // the aircraft's own colour so it is clear what they are queued on.
          border: isOption ? `1px dashed ${base}` : '1px solid rgba(0,0,0,0.1)',
          color: isOption ? theme.palette.text.primary : theme.palette.getContrastText(base),
          borderRadius: 6,
          fontSize: 12,
        },
      }
    },
    [theme],
  )

  // A booking on a restricted aircraft keeps its slot but carries a warning
  // icon that opens the defect; AOG days read "AOG" in the title.
  const EventView = useCallback(
    ({ event, title }: EventProps<CalendarEvent>) => {
      const status = statuses?.get(event.registration)
      const warn = status && status.light === 'restricted' && !event.aog
      return (
        <span>
          {event.maintenance ? <Icon icon="mdi:wrench" width={14} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
          {/* A booking, not a block: its type, so private, lesson and
              maintenance read apart without opening them. */}
          {!event.maintenance && !event.aog && event.booking.bookingId ? (
            <Icon icon={BOOKING_TYPE_ICONS[typeOf(event.booking)]} width={14} style={{ verticalAlign: -2, marginRight: 4 }} />
          ) : null}
          {event.booking.isFirePatrol ? <Icon icon="mdi:fire" width={14} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
          {event.aog ? <Icon icon="mdi:hand-front-left" width={14} style={{ verticalAlign: -2, marginRight: 4 }} /> : null}
          {warn ? (
            <Icon
              icon="mdi:alert-outline"
              width={14}
              role="button"
              aria-label={t('calendar.openDefects')}
              style={{ verticalAlign: -2, marginRight: 4, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation()
                onOpenDefects?.(event.registration)
              }}
            />
          ) : null}
          {event.onAogDays ? `AOG · ${title}` : title}
        </span>
      )
    },
    [statuses, onOpenDefects, t],
  )

  // Time-grid views (day, week, ten days) shade each slot by the light at its
  // middle: amber for twilight, blue for night. Offline, from sunTimes.
  const slotPropGetter = useCallback(
    (slot: Date) =>
      slotDaylight(slot, place, {
        from: (time) => t('calendar.nightFrom', { time }),
        until: (time) => t('calendar.nightUntil', { time }),
      }),
    [t, place],
  )

  // Sunrise and sunset under each day's name in the time-grid views.
  const sunLabels = useMemo(
    () => ({
      sunrise: t('calendar.sunrise', { time: '' }).trim(),
      sunset: t('calendar.sunset', { time: '' }).trim(),
      polarNight: t('calendar.polarNight'),
      midnightSun: t('calendar.midnightSun'),
    }),
    [t],
  )
  const DayHeader = useCallback(
    ({ date: day, label }: { date: Date; label: string }) => (
      <span>
        {label}
        {view === Views.MONTH ? null : (
          <small style={{ display: 'block', fontWeight: 400, opacity: 0.8, lineHeight: 1.3, whiteSpace: 'normal' }}>
            <SunLine dayKey={dayjs(day).format('YYYY-MM-DD')} place={place} labels={sunLabels} />
          </small>
        )}
      </span>
    ),
    [view, sunLabels, place],
  )

  // The empty top-left cell: which clock the grid is on. The offset is the
  // one in force on the displayed day, so it reads UTC+2 after the clock change.
  const TimeZoneHeader = useCallback(() => {
    const zone = getClubTimezone()
    return (
      <Tooltip title={`${t('booking.timeZoneNote', { zone: '' }).trim()} · ${timezoneLabel(zone)}`}>
        <span className="calendar-zone">
          {formatUtcOffset(date, zone)}
          <small>{timezoneLabel(zone)}</small>
        </span>
      </Tooltip>
    )
  }, [date, t])

  // Hover (mouse) shows who, phone and notes; a click or tap opens the same
  // in a popover. AOG and maintenance blocks are not bookings: left alone.
  const EventWrapper = useCallback(
    ({ event, children }: { event: CalendarEvent; children?: React.ReactNode }) =>
      event.aog || event.maintenance || !event.booking.bookingId ? (
        <>{children}</>
      ) : (
        <BookingHover booking={event.booking}>{children as React.ReactElement}</BookingHover>
      ),
    [],
  )

  const messages = useMemo(
    () => ({
      today: t('common.today'),
      previous: t('calendar.previous'),
      next: t('calendar.next'),
      month: t('calendar.month'),
      week: t('calendar.week'),
      [TEN_DAY_VIEW]: t('calendar.tenDays'),
      day: t('calendar.day'),
      agenda: t('calendar.agenda'),
      date: t('booking.starts'),
      time: t('booking.duration'),
      event: t('booking.title'),
      noEventsInRange: t('calendar.noBookings'),
      allDay: '',
      showMore: (count: number) => `+${count}`,
    }),
    [t],
  )

  return (
    <Box
      ref={boxRef}
      sx={{
        '--rbc-cols': (view as string) === TEN_DAY_VIEW ? 10 : 7,
        '--rbc-surface': theme.palette.background.default,
        // rbc sizes itself off its container, so the height has to be
        // explicit. Taller on desktop where the whole working day should
        // fit without scrolling.
        height: { xs: 520, md: 680 },
        '& .rbc-calendar': { fontFamily: theme.typography.fontFamily },
        // The list view paints the first booking's colour behind the day's whole
        // date cell (the row's style covers the cell that spans the day). The
        // date is not that booking's: plain surface.
        '& .rbc-agenda-date-cell': { backgroundColor: 'var(--rbc-surface)', width: 84 },
        // The event's own border is drawn on the row, where it pokes out beside the
        // date cell; in a list the fill says enough.
        '& .rbc-agenda-table tbody > tr': { border: 'none !important' },
      }}
    >
      <Calendar<CalendarEvent>
        localizer={localizer}
        formats={formats}
        culture={i18n.language}
        events={[...events, ...ownOptionEvents, ...optionSummary, ...aogEvents, ...maintenanceEvents]}
        components={{ event: EventView, eventWrapper: EventWrapper as never, timeGutterHeader: TimeZoneHeader, header: DayHeader, agenda: { date: AgendaDate } }}
        slotPropGetter={slotPropGetter}
        view={view}
        date={date}
        onView={onView}
        onNavigate={onNavigate}
        // rbc's types know only its own four views; a custom one is a component.
        views={{ month: true, week: true, [TEN_DAY_VIEW]: TenDay, day: true, agenda: true } as never}
        messages={messages}
        popup
        selectable={Boolean(onSelectSlot)}
        onSelectEvent={(event, e) => {
          if (event.maintenance) return
          if (event.aog) return onOpenDefects?.(event.registration)
          const anchor = (e?.currentTarget as HTMLElement | undefined) ?? document.body
          peek.open(event.booking, anchor)
        }}
        // A drag across month cells ends at midnight after the last day
        // dragged, so it books whole days; dropping `end` there left only the
        // default two hours on the first day (#252).
        onSelectSlot={(slot) => onSelectSlot?.(slot.start as Date, slot.action === 'select' ? (slot.end as Date) : undefined)}
        eventPropGetter={eventPropGetter}
        // The club does not fly at 03:00; starting the scroll at 07:00 puts
        // the working day on screen without the member scrolling first.
        scrollToTime={scrollAnchor(date)}
        // 'overlap', not 'no-overlap': the latter divides the column
        // equally between every concurrent event, so a booking with two
        // options queued on it becomes three slivers of vertical text.
        // Overlapping offsets them instead, keeping the reservation
        // readable and the options visibly stacked behind it.
        dayLayoutAlgorithm="overlap"
      />
      {peek.element}
    </Box>
  )
}

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString()
}

function scrollAnchor(date: Date): Date {
  const anchor = new Date(date)
  anchor.setHours(7, 0, 0, 0)
  return anchor
}

// The list view's date cell, laid out like the flight log's date: the weekday
// above the large day number and the month below it. Noon, so the club's zone
// and the browser's agree on the day.
function AgendaDate({ day }: { day: Date }) {
  const { i18n } = useTranslation()
  const noon = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 12)
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(i18n.language, { timeZone: getClubTimezone(), ...options }).format(noon)
  const small = { fontSize: { xs: 10, sm: 14 }, color: 'text.secondary', lineHeight: 1.2 }
  return (
    <Box sx={{ textAlign: 'center', minWidth: { xs: 36, sm: 56 } }}>
      <Typography sx={{ ...small, textTransform: 'uppercase' }}>{part({ weekday: 'short' })}</Typography>
      <Typography sx={{ fontSize: { xs: 28, sm: 40 }, fontWeight: 700, lineHeight: 1 }}>{part({ day: 'numeric' })}</Typography>
      <Typography sx={{ ...small, textTransform: 'uppercase' }}>{part({ month: 'short' })}</Typography>
    </Box>
  )
}
